import React, { useEffect, useRef, useState } from 'react'
import { Prompt, useHistory, useLocation } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import FormControlLabel from '@mui/material/FormControlLabel'
import Radio from '@mui/material/Radio'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import FormControl from '@mui/material/FormControl'
import Typography from '@mui/material/Typography'
import Add from '@mui/icons-material/Add'
import Title from '../../component/Title'
import API from '../../component/Axios'
import Snackbars from '../../component/Snackbars'
import BottomButtonGroup, { FORM_WIDE_UPLOAD_BUTTON_SX } from '../../component/BottomButtonGroup'
import { FORM_IMAGE_PREVIEW_FRAME_SX, FORM_PANEL_SX, getFormChoiceCardSx } from '../../component/formSurfaceStyles'
import { getLocationState } from '../../utils/listPageState'
import {
    APP_BANNER_IMAGE_FILE_ACCEPT,
    APP_BANNER_IMAGE_FILE_ERROR_MESSAGE,
    APP_BANNER_IMAGE_FIELD_CONFIGS,
    applyAppBannerFieldChange,
    applyAppBannerDraft,
    clearAppBannerDraft,
    createDefaultAppBannerParams,
    createAppBannerDraftPayload,
    handleAppBannerBackNavigation,
    handleAppBannerImageUpload,
    moveToAppBannerList,
    NEW_APP_BANNER_DRAFT_KEY,
    prepareAppBannerSubmit,
    readAppBannerDraft,
    saveAppBannerDraft,
} from './appBannerDraftUtils'
import {
    APP_BANNER_IMAGE_LEAVE_MESSAGE,
    hasPendingAppBannerImages,
    usePendingAppBannerImageUnloadGuard,
} from './appBannerLeaveGuard'

const PANEL_SX = FORM_PANEL_SX

const IMAGE_PREVIEW_FRAME_SX = {
    ...FORM_IMAGE_PREVIEW_FRAME_SX,
    width: '100%',
    height: { xs: 220, md: 280 },
    mb: 1.5,
}

const IMAGE_PREVIEW_SX = {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    display: 'block',
}

const getChoiceCardSx = (selected, disabled = false) => ({
    ...getFormChoiceCardSx(selected, disabled),
    p: 1.5,
    width: '100%',
})

const APP_BANNER_TYPE_OPTIONS = [
    {
        value: 1,
        title: '거리전',
        description: '거리전 탭 기준으로 App 배너를 노출합니다.',
    },
    {
        value: 2,
        title: '속도전',
        description: '속도전 탭 기준으로 App 배너를 노출합니다.',
    },
    {
        value: 3,
        title: '별주부전',
        description: '별주부전 탭 기준으로 App 배너를 노출합니다.',
    },
    {
        value: 4,
        title: '우리끼리대회',
        description: '우리끼리대회 영역 기준으로 App 배너를 노출합니다.',
    },
]

const APP_BANNER_COUNTRY_OPTIONS = [
    {
        value: 'kr',
        title: '국내',
        description: '국내 사용자 화면에 노출합니다.',
    },
    {
        value: 'us',
        title: '해외',
        description: '해외 사용자 화면에 노출합니다.',
    },
]

const getImageFileName = (imageFile) => {
    if (!imageFile || typeof imageFile !== 'object') {
        return ''
    }

    return imageFile.name || ''
}

function FormSection({ title, description, children }) {
    return (
        <Paper variant="outlined" sx={PANEL_SX}>
            <Box sx={{ mb: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    {title}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    {description}
                </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {children}
            </Box>
        </Paper>
    )
}

function ChoiceCardLabel({ title, description }) {
    return (
        <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                {title}
            </Typography>
            <Typography variant="caption" color="text.secondary">
                {description}
            </Typography>
        </Box>
    )
}

// 카드형 선택 UI에서도 기존 폼 변경 핸들러가 받던 name/value 이벤트 계약을 유지한다.
const createFieldChangeEvent = (name, value) => ({
    target: {
        name,
        value,
    },
})

function SettingChoiceGroup({ title, name, value, options, disabled, onChange }) {
    return (
        <FormControl fullWidth>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                {title}
            </Typography>
            <Box
                role="radiogroup"
                aria-label={title}
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                    gap: 1,
                    mt: 1,
                }}
            >
                {options.map((option) => {
                    const checked = String(value) === String(option.value)

                    return (
                        <FormControlLabel
                            key={option.value}
                            value={option.value}
                            control={(
                                <Radio
                                    name={name}
                                    checked={checked}
                                    onChange={() => onChange(createFieldChangeEvent(name, option.value))}
                                    disabled={disabled}
                                />
                            )}
                            disabled={disabled}
                            sx={getChoiceCardSx(checked, disabled)}
                            label={(
                                <ChoiceCardLabel
                                    title={option.title}
                                    description={option.description}
                                />
                            )}
                        />
                    )
                })}
            </Box>
        </FormControl>
    )
}

function AppBannerImageUploadPanel({
    field,
    imageSrc,
    imageFile,
    disabled,
    onUploadClick,
}) {
    const imageFileName = getImageFileName(imageFile)

    return (
        <Paper variant="outlined" sx={PANEL_SX}>
            <Box sx={{ mb: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    {field.previewAlt}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    App 배너에 노출되는 이미지를 등록합니다.
                </Typography>
            </Box>
            <Box sx={IMAGE_PREVIEW_FRAME_SX}>
                {imageSrc ? (
                    <Box
                        component="img"
                        src={imageSrc}
                        alt={`${field.previewAlt} 미리보기`}
                        sx={IMAGE_PREVIEW_SX}
                    />
                ) : (
                    <Typography variant="body2" color="text.secondary">
                        이미지를 선택하면 미리보기가 표시됩니다.
                    </Typography>
                )}
            </Box>
            <FormControl fullWidth>
                <TextField
                    inputProps={{ accept: APP_BANNER_IMAGE_FILE_ACCEPT }}
                    style={{ display: 'none' }}
                    name={field.fieldName}
                    id={field.fieldName}
                    type="file"
                    disabled={disabled}
                    onChange={(event) => onUploadClick(event, field.imageType)}
                />
                <Box component="label" htmlFor={field.fieldName} sx={{ display: 'block' }}>
                    <Button
                        fullWidth
                        variant="contained"
                        color="success"
                        component="span"
                        startIcon={<Add />}
                        sx={FORM_WIDE_UPLOAD_BUTTON_SX}
                        disabled={disabled}
                    >
                        {field.uploadLabel}
                    </Button>
                </Box>
            </FormControl>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                {imageFileName ? `선택한 파일: ${imageFileName}` : '저장 전 미리보기로 이미지가 올바른지 확인하세요.'}
            </Typography>
        </Paper>
    )
}

export default function NewAppBanner() {
    const history = useHistory()
    const location = useLocation()
    const locationState = getLocationState(location)
    const curPage = locationState.curPage
    const searchText = locationState.searchText
    const searchSelect = locationState.searchSelect
    const listSearch = location.search || ''
    const [saving, setSaving] = useState(false)
    const [params, setParams] = useState(() => createDefaultAppBannerParams())
    const [mainImage, setMainImage] = useState('')
    const [subImage, setSubImage] = useState('')
    const [openSnackbar, setOpenSnackbar] = useState(false)
    const [severitySnackbar, setSeveritySnackbar] = useState('info')
    const [messageSnackbar, setMessageSnackbar] = useState('')
    const [draftReady, setDraftReady] = useState(false)
    const [navigationUnlocked, setNavigationUnlocked] = useState(false)
    const initialDraftSnapshotRef = useRef('')
    const leaveGuardEnabled = hasPendingAppBannerImages(params) && !saving && !navigationUnlocked

    usePendingAppBannerImageUnloadGuard(leaveGuardEnabled)

    useEffect(() => {
        const baseParams = createDefaultAppBannerParams()
        const storedDraft = readAppBannerDraft(NEW_APP_BANNER_DRAFT_KEY)
        const restoredDraft = applyAppBannerDraft({
            baseParams,
            draft: storedDraft,
        })

        initialDraftSnapshotRef.current = JSON.stringify(createAppBannerDraftPayload({
            params: baseParams,
        }))

        setParams(prev => ({
            ...prev,
            ...restoredDraft.params,
        }))
        setDraftReady(true)
    }, [])

    useEffect(() => {
        if (!draftReady) {
            return
        }

        // App 배너 등록은 목록 query를 건드리지 않고 폼 draft만 sessionStorage에 보존한다.
        const nextDraft = createAppBannerDraftPayload({
            params,
        })
        const nextDraftSnapshot = JSON.stringify(nextDraft)

        if (nextDraftSnapshot === initialDraftSnapshotRef.current) {
            clearAppBannerDraft(NEW_APP_BANNER_DRAFT_KEY)
            return
        }

        saveAppBannerDraft(NEW_APP_BANNER_DRAFT_KEY, nextDraft)
    }, [draftReady, params])

    const handleClose = () => {
        setOpenSnackbar(false)
    }

    const moveToList = (snackbarState) => {
        moveToAppBannerList({
            draftKey: NEW_APP_BANNER_DRAFT_KEY,
            clearDraft: clearAppBannerDraft,
            setNavigationUnlocked,
            history,
            listSearch,
            snackbarState,
            curPage,
            searchText,
            searchSelect,
        })
    }

    const handleBackClick = (event) => {
        handleAppBannerBackNavigation({
            event,
            leaveGuardEnabled,
            draftKey: NEW_APP_BANNER_DRAFT_KEY,
            clearDraft: clearAppBannerDraft,
            confirmLeave: () => window.confirm(APP_BANNER_IMAGE_LEAVE_MESSAGE),
            moveToList: () => moveToList(),
        })
    }

    const handleUpdateField = (e) => {
        setParams(prev => applyAppBannerFieldChange({
            params: prev,
            name: e.target.name,
            value: e.target.value,
        }))
    }

    const handleUploadClick = (e, type) => {
        handleAppBannerImageUpload({
            event: e,
            imageType: type,
            setParams,
            setMainImage,
            setSubImage,
            onInvalidFile: (message) => {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(message || APP_BANNER_IMAGE_FILE_ERROR_MESSAGE)
            },
        })
    }

    const createData = async (params) => {
        try {
            setSaving(true)
            const { data } = await API({
                method: 'post',
                url: '/api/v1/admin/appBanner/create',
                data: params,
                headers: { "Content-Type": "multipart/form-data" }
            })
            if (data.code === 200) {
                // 등록 후에도 이전 목록 필터와 페이지를 유지한 채 성공 메시지를 보여준다.
                moveToList({
                    severity: 'success',
                    message: data.message,
                })
            } else if (data.code === 403) {
                history.push('/login')
            } else {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(data.message)
            }
        } catch (e) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('App 배너를 등록하지 못했습니다.')
        } finally {
            setSaving(false)
        }
    }

    const handleSubmit = (e) => {
        e.preventDefault()

        if (saving) {
            return
        }

        const { nextParams, errorMessage, formData } = prepareAppBannerSubmit({
            params,
            requireMainImage: true,
        })

        setParams(nextParams)

        if (errorMessage) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(errorMessage)
            return
        }

        createData(formData)
    }

    return (
        <Grid item xs={12}>
            <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                <Title>App 배너 등록</Title>
                <Prompt when={leaveGuardEnabled} message={APP_BANNER_IMAGE_LEAVE_MESSAGE} />
                <Box component="form" encType="multipart/form-data" onSubmit={handleSubmit} sx={{ opacity: saving ? 0.6 : 1 }}>
                    <Grid container spacing={2} sx={{ marginTop: 2 }}>
                        {APP_BANNER_IMAGE_FIELD_CONFIGS.map((field) => (
                            <Grid key={field.fieldName} item xs={12} md={6}>
                                <AppBannerImageUploadPanel
                                    field={field}
                                    imageSrc={field.fieldName === 'mainImage' ? mainImage : subImage}
                                    imageFile={params[field.fieldName]}
                                    disabled={saving}
                                    onUploadClick={handleUploadClick}
                                />
                            </Grid>
                        ))}
                        <Grid item xs={12} md={7}>
                            <FormSection
                                title="배너 내용"
                                description="App 배너 목록과 사용자 화면에 표시할 제목과 내용을 작성합니다."
                            >
                                <FormControl fullWidth>
                                    <TextField
                                        required
                                        label="제목"
                                        id="title"
                                        name="title"
                                        value={params.title || ''}
                                        onChange={handleUpdateField}
                                        disabled={saving}
                                        size="small"
                                    />
                                </FormControl>
                                <FormControl fullWidth>
                                    <TextField
                                        label="내용"
                                        multiline
                                        rows={8}
                                        id="content"
                                        name="content"
                                        value={params.content || ''}
                                        onChange={handleUpdateField}
                                        disabled={saving}
                                        size="small"
                                    />
                                </FormControl>
                            </FormSection>
                        </Grid>
                        <Grid item xs={12} md={5}>
                            <FormSection
                                title="노출 설정"
                                description="배너가 표시될 위치와 대상 국가를 함께 설정합니다."
                            >
                                <SettingChoiceGroup
                                    title="노출 위치"
                                    name="type"
                                    value={params.type || 1}
                                    options={APP_BANNER_TYPE_OPTIONS}
                                    disabled={saving}
                                    onChange={handleUpdateField}
                                />
                                <SettingChoiceGroup
                                    title="노출 나라"
                                    name="cCode"
                                    value={params.cCode || 'kr'}
                                    options={APP_BANNER_COUNTRY_OPTIONS}
                                    disabled={saving}
                                    onChange={handleUpdateField}
                                />
                            </FormSection>
                        </Grid>
                    </Grid>
                    <BottomButtonGroup path="/appBanners" search={listSearch} visible={true} curPage={curPage} searchText={searchText} searchSelect={searchSelect} submitDisabled={saving} submitLabel={saving ? '저장 중...' : '저장'} onBackClick={handleBackClick} />
                </Box>
                {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
            </Paper>
        </Grid>
    )
}
